import {
  formatQuoteBrl,
  formatQuoteCrypto,
  formatQuotePercent,
  formatQuotePrice,
  formatQuoteTimer,
  QUOTE_TITLES,
} from "../stablesQuoteDisplay";
import { resolveBuyQuoteDisplay } from "../constants";

const QuoteRow = ({ label, value, suffix, muted }) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-200/80 py-3 last:border-b-0 dark:border-brand-dark-border">
    <span className={`text-sm font-bold ${muted ? "text-gray-500" : "text-brand-dark"}`}>
      {label}
    </span>
    <span className={`text-right text-sm font-bold ${muted ? "text-gray-500" : "text-brand-dark"}`}>
      {value}{" "}
      {suffix && <span className="font-semibold text-gray-400">{suffix}</span>}
    </span>
  </div>
);

const QuoteTimer = ({ secondsLeft, barTransition, barWidth }) => (
  <div className="mt-4 flex flex-col items-center gap-2">
    <span className="rounded-full bg-gray-100 px-4 py-1 text-xs font-medium text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300">
      Tempo restante: {formatQuoteTimer(secondsLeft)}
    </span>
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-brand-dark-border">
      <div
        className={`h-full rounded-full ${barTransition ? "transition-all duration-1000 ease-linear" : ""}`}
        style={{
          width: `${barWidth}%`,
          background: "linear-gradient(135deg, #fcd177, #e7a73f)",
        }}
      />
    </div>
  </div>
);

const buildFeeRows = (quote, suffix) => {
  if (quote?.feePercent == null) return [];

  return [
    {
      label: "Taxa",
      value: quote.feeStable != null ? formatQuoteCrypto(quote.feeStable) : formatQuoteBrl(quote.feeBrl),
      suffix: quote.feeStable != null ? suffix : "BRL",
      muted: true,
    },
    {
      label: "Percentual da taxa",
      value: formatQuotePercent(quote.feePercent),
      suffix: "%",
      muted: true,
    },
  ];
};

const buildTradeRows = (operation, quote) => {
  const isBuy = operation === "buy";
  const buyDisplay = isBuy ? resolveBuyQuoteDisplay(quote) : null;
  const feeSuffix = isBuy ? quote.assetLabel : quote.assetLabel;

  return [
    {
      label: "Preço",
      value: formatQuotePrice(quote.price),
      suffix: "BRL",
    },
    isBuy
      ? {
          label: "Você compra",
          value: formatQuoteCrypto(buyDisplay.stableAmount),
          suffix: quote.assetLabel,
        }
      : {
          label: "Você vende",
          value: formatQuoteCrypto(quote.stableAmount),
          suffix: quote.assetLabel,
        },
    isBuy
      ? {
          label: "Você paga",
          value: formatQuoteBrl(buyDisplay.brlAmount),
          suffix: "BRL",
        }
      : {
          label: "Você recebe",
          value: formatQuoteBrl(quote.netBrlAmount),
          suffix: "BRL",
        },
    ...buildFeeRows(quote, feeSuffix),
  ];
};

const buildSwapRows = (quote) => {
  const fromAmount = Number(quote.fromAmount) || 0;
  const netToAmount = Number(quote.netToAmount) || 0;
  const swapRate = fromAmount > 0 ? netToAmount / fromAmount : Number(quote.price) || 0;

  return [
    {
      label: "Preço",
      value: formatQuoteCrypto(swapRate),
      suffix: quote.toAssetLabel,
    },
    {
      label: "Enviado",
      value: formatQuoteCrypto(quote.fromAmount),
      suffix: quote.fromAssetLabel,
    },
    {
      label: "Recebido",
      value: formatQuoteCrypto(quote.netToAmount),
      suffix: quote.toAssetLabel,
    },
    ...buildFeeRows(quote, quote.toAssetLabel),
  ];
};

const buildWithdrawRows = (quote) => {
  const rows = [
    {
      label: "Rede",
      value: quote.chain || "—",
    },
    {
      label: "Destino",
      value: quote.destinationDisplay || quote.destination || "—",
    },
    {
      label: "Você saca",
      value: formatQuoteCrypto(quote.stableAmount),
      suffix: quote.assetLabel,
    },
    {
      label: "Você recebe",
      value: formatQuoteCrypto(quote.netStableAmount),
      suffix: quote.assetLabel,
    },
    {
      label: "Taxa",
      value: formatQuoteCrypto(quote.feeStable),
      suffix: quote.assetLabel,
      muted: true,
    },
  ];

  if (Number(quote.networkFeeStable) > 0) {
    rows.push({
      label: "Taxa da rede",
      value: formatQuoteCrypto(quote.networkFeeStable),
      suffix: quote.assetLabel,
      muted: true,
    });
  }

  rows.push({
    label: "Percentual da taxa",
    value: formatQuotePercent(quote.feePercent),
    suffix: "%",
    muted: true,
  });

  return rows;
};

export const StablesOperationQuoteSummary = ({
  operation,
  quote,
  showTimer = false,
  timerProps = null,
}) => {
  if (!quote || !["buy", "sell", "swap", "withdraw"].includes(operation)) return null;

  const rows =
    operation === "swap"
      ? buildSwapRows(quote)
      : operation === "withdraw"
        ? buildWithdrawRows(quote)
        : buildTradeRows(operation, quote);

  return (
    <div className="flex-1">
      <h3 className="text-center text-base font-bold text-brand-dark">
        {QUOTE_TITLES[operation]}
      </h3>

      <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        {rows.map((row) => (
          <QuoteRow key={row.label} {...row} />
        ))}
      </div>

      {showTimer && timerProps && <QuoteTimer {...timerProps} />}
    </div>
  );
};
